import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileCsv } from "@phosphor-icons/react";
import { AdminHeader } from "@/components/admin/parts";
import { TopFailures, Alerts, EnquiryCorrelation } from "@/components/admin/wi/ReliabilityPanels";
import { SystemChart, ModelTable, ProductionYears, RepeatCard } from "@/components/admin/wi/ReliabilityCharts";
import { Stat, SYSTEMS, aud, btnGhost, num, pct, selectCls } from "@/components/admin/wi/ui";
import { adminApi, getErrorMessage } from "@/lib/api";

function Filters({ f, setF, options }) {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="wi-reliability-filters">
      <select value={f.model} onChange={set("model")} className={selectCls} data-testid="wi-filter-model"><option value="">All models</option>{(options?.models || []).map((m) => <option key={m}>{m}</option>)}</select>
      <select value={f.system} onChange={set("system")} className={selectCls} data-testid="wi-filter-system"><option value="">All systems</option>{SYSTEMS.map((s) => <option key={s}>{s}</option>)}</select>
      <select value={f.year} onChange={set("year")} className={selectCls} data-testid="wi-filter-year"><option value="">All production years</option>{(options?.years || []).map((y) => <option key={y}>{y}</option>)}</select>
      <label className="text-[0.65rem] text-neutral-500">From<input type="date" value={f.start} onChange={set("start")} className={`${selectCls} block`} data-testid="wi-filter-start" /></label>
      <label className="text-[0.65rem] text-neutral-500">To<input type="date" value={f.end} onChange={set("end")} className={`${selectCls} block`} data-testid="wi-filter-end" /></label>
      {Object.values(f).some(Boolean) && <button onClick={() => setF({ model: "", system: "", year: "", start: "", end: "" })} className="text-xs text-neutral-500 hover:text-white pb-2">Clear</button>}
    </div>
  );
}

export default function WiReliability() {
  const [f, setF] = useState({ model: "", system: "", year: "", start: "", end: "" });
  const [rankBy, setRankBy] = useState("frequency");
  const [d, setD] = useState(null);
  const load = useCallback(() => adminApi.get("/admin/wi/reliability", { params: { ...f, rank_by: rankBy } }).then(({ data }) => setD(data)).catch(() => {}), [f, rankBy]);
  useEffect(() => { load(); }, [load]);
  const exportCsv = async () => {
    try {
      const { data } = await adminApi.get("/admin/wi/reliability/export", { params: f, responseType: "blob" });
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(data), download: "otm-warranty-faults.csv" });
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(getErrorMessage(e)); }
  };
  return (
    <div data-testid="wi-reliability-page">
      <AdminHeader title="Product Reliability" subtitle="Recurring faults, component failures and emerging issues from historical and ongoing warranty data"
        actions={<button className={btnGhost} onClick={exportCsv} data-testid="wi-export-csv"><FileCsv size={15} /> Export CSV</button>} />
      <div className="p-8 space-y-6">
        <Filters f={f} setF={setF} options={d?.filters} />
        {!d ? <div className="h-60 animate-pulse bg-white/[0.02] rounded-2xl" /> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label="Reported faults" value={num(d.totals.faults)} sub={`${num(d.totals.claims)} claims`} testId="wi-rel-faults" />
              <Stat label="Caravans affected" value={num(d.totals.caravans)} />
              <Stat label="Warranty cost" value={aud(d.totals.total_cost)} sub="approved, inc GST" tone="gold" />
              <Stat label="Avg cost per fault" value={aud(d.totals.avg_cost)} tone="gold" />
              <Stat label="Repeat rate" value={`${d.repeat.repeat_rate_pct}%`} tone="amber" />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="wi-rel-windows">
              {d.windows.map((w) => (
                <div key={w.window} className="bg-black/30 border border-neutral-800 rounded-xl px-3 py-2 text-xs">
                  <span className="text-neutral-500 uppercase tracking-wider text-[0.6rem] font-bold mr-2">{w.window === "all" ? "All time" : `Last ${w.window}`}</span>
                  <span className="text-white font-semibold tabular-nums">{num(w.faults)}</span>
                  {w.change_pct !== null && <span className={`ml-1.5 ${w.change_pct > 0 ? "text-amber-400" : "text-neutral-500"}`}>{pct(w.change_pct)}</span>}
                </div>
              ))}
            </div>
            <TopFailures rows={d.top} rankBy={rankBy} onRank={setRankBy} />
            <div className="grid xl:grid-cols-2 gap-6 items-start"><Alerts alerts={d.alerts} /><SystemChart rows={d.by_system} /></div>
            <div className="grid xl:grid-cols-2 gap-6 items-start"><ModelTable rows={d.by_model} /><ProductionYears rows={d.by_production_year} note={`${d.notes.production_year} ${d.notes.per_100}`} /></div>
            <div className="grid xl:grid-cols-2 gap-6 items-start"><RepeatCard r={d.repeat} /><EnquiryCorrelation rows={d.enquiries} /></div>
          </>
        )}
      </div>
    </div>
  );
}
